import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import FullGridCalendar from './FullGridCalender';
import { CalendarEvent, CalendarTheme, CalendarSectionProps } from '../types';
import { useTheme } from '../../../theme';
import { DARK_THEME } from './FullGridCalender/constants';

const CalendarSection: React.FC<CalendarSectionProps> = ({
  events,
  onDateSelect,
  onEventPress,
  onEventRemove,
  onEventDetailsPress,
  onShowAllEvents,
  viewMode = 'monthly',
  theme = {},
  onNavigationChange,
  onViewModeChange,
  onViewDetails,
  isLoading = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  // Apply dark theme when resolvedTheme is 'dark' (works on all platforms including web)
  const isDark = resolvedTheme === 'dark';

  const defaultTheme: CalendarTheme = isDark
    ? DARK_THEME
    : {
        backgroundColor: colors.background.page,
        surfaceColor: colors.background.surface,
        headerTextColor: colors.text.primary,
        dayNumberColor: colors.text.primary,
        disabledDayColor: colors.text.disabled,
        borderColor: colors.border.default,
        controlBackgroundColor: '#D8E7FB',
        controlActiveBackgroundColor: colors.brand.primary,
        controlTextColor: colors.brand.primary,
        controlActiveTextColor: colors.text.inverse,
        controlBorderColor: '#D8E7FB',
        chevronColor: colors.text.primary,
        todayAccentColor: colors.brand.primary,
        disabledBackgroundColor: '#fafafa',
        overflowBadgeBackgroundColor: '#e8f0ff',
        overflowBadgeTextColor: colors.text.primary,
        shadowColor: 'rgba(58, 58, 58, 0.08)',
        eventColors: {
          pending: '#4caf50',
          done: '#cccccc',
          default: '#ffc107',
        },
        containerStyle: {
          minHeight: 'auto',
        },
      };

  const mergedTheme = { ...defaultTheme, ...theme };
  const calendarWrapperStyle = useMemo(
    () => [styles.calendarWrapper, { backgroundColor: mergedTheme.backgroundColor }, getCalendarWrapperShadow(mergedTheme.shadowColor)],
    [mergedTheme.backgroundColor, mergedTheme.shadowColor]
  );

  return (
    <View style={calendarWrapperStyle}>
      <FullGridCalendar
        events={events}
        onDateSelect={onDateSelect}
        onEventPress={onEventPress}
        onEventRemove={onEventRemove}
        onEventDetailsPress={onEventDetailsPress}
        onShowAllEvents={onShowAllEvents}
        onNavigationChange={onNavigationChange}
        onViewModeChange={onViewModeChange}
        onViewDetails={onViewDetails}
        theme={mergedTheme}
        viewMode={viewMode}
        isLoading={isLoading}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  calendarWrapper: {
    borderRadius: 8,
    overflow: 'hidden',
  },
});

const getCalendarWrapperShadow = (shadowColor?: string) =>
  Platform.select({
    ios: {
      shadowColor: shadowColor || 'rgba(58, 58, 58, 0.08)',
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 1,
      shadowRadius: 16,
    },
    android: {
      elevation: 4,
      shadowColor: shadowColor || 'rgba(58, 58, 58, 0.08)',
    },
    web: {
      boxShadow: `0px 6px 16px 0px ${shadowColor || 'rgba(58, 58, 58, 0.08)'}`,
    },
    default: {},
  });

export default CalendarSection;
